import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { StatusPage } from './components/StatusPage/StatusPage';
import ErrorBoundary from './components/ErrorBoundary';
import AdminPage from './components/AdminPage/AdminPage';
import { useKeycloak } from '@react-keycloak/web';
import { ReactNode, useState, useEffect } from 'react';
import { useUserRoles } from './utils/rolesHelper.ts';
import { UserRoles } from './models/roles.ts';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from './store';
import { fetchKeycloakPodMetrics } from './store/slices/keycloakPodMetricSlice';
import LoginForm from './components/AdminPage/LoginForm';


// Protected route component that checks authentication
interface ProtectedRouteProps {
  children: ReactNode;
  isSystemAdmin?: boolean;
}

const ProtectedRoute = ({ children, isSystemAdmin }: ProtectedRouteProps) => {
  const { keycloak, initialized } = useKeycloak();
  const dispatch = useAppDispatch();
  const [keycloakAvailable, setKeycloakAvailable] = useState(true);
  const [isLocallyAuthenticated, setIsLocallyAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Get keycloak pod metrics to check if keycloak is running
  const keycloakPodMetrics = useSelector((state: RootState) => state.keycloakPodMetrics);

  // Initialize and check authentication state
  useEffect(() => {
    // Check if user is locally authenticated
    const localAuthRaw = localStorage.getItem('isAuthenticated');
    let localAuth = false;
    if (localAuthRaw) {
      try {
        // check if the local auth is expired
        const localAuthObj = JSON.parse(localAuthRaw);
        if (localAuthObj.expiration && Date.now() > localAuthObj.expiration) {
          localStorage.removeItem('isAuthenticated');
        } else {
          localAuth = !!localAuthObj.isAuthenticated;
        }
      } catch (e) {
        localStorage.removeItem('isAuthenticated');
      }
    }
    // set the local auth state
    setIsLocallyAuthenticated(localAuth);
    
    // Fetch keycloak pod metrics
    dispatch(fetchKeycloakPodMetrics('prod'));
  }, [dispatch]);

  // Process keycloak availability and complete initialization
  useEffect(() => {
    if (!initialized) return;
    
    // Check keycloak availability based on pod metrics
    if (keycloakPodMetrics.metrics.length > 0) {
      const anyKeycloakPodNotRunning = keycloakPodMetrics.metrics.some(
        pod => pod.container_name === 'keycloak' && pod.phase !== 'Running'
      );
      setKeycloakAvailable(!anyKeycloakPodNotRunning);
    }
    
    setIsLoading(false);
  }, [initialized, keycloakPodMetrics.metrics]);

  // Show loading while initializing
  if (isLoading || !initialized) {
    return <div>Loading...</div>;
  }
  
  // Local authentication takes precedence
  if (isLocallyAuthenticated) {
    return <>{children}</>;
  }
  
  // Handle Keycloak authentication
  if (keycloak && !keycloak.authenticated) {
    if (keycloakAvailable) {
      keycloak.login();
      return <div>Redirecting to Keycloak login...</div>;
    } else {
      return <LoginForm />;
    }
  }
  
  // Check admin permissions for authenticated users
  if (!isSystemAdmin) {
    return <Navigate to="/" replace />;
  }
  
  // If authenticated and has proper permissions, show the content
  return <>{children}</>;
};

function App() {
  const { hasRole } = useUserRoles();
  const isSystemAdmin = hasRole(UserRoles.Administrator);

  return (
    <ErrorBoundary>
      <Router>
        <Routes>
          <Route path="/" element={<StatusPage />} />
          <Route path="/admin" element={
            <ProtectedRoute isSystemAdmin={isSystemAdmin}>
              <AdminPage />
            </ProtectedRoute>
          } />
        </Routes>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
